import React, { useState, useEffect } from 'react';
import { getApiBaseUrl } from '../../../config/apiBase';
import * as Icons from 'lucide-react';
import UniversalSearchInput from '../../../components/common/UniversalSearchInput';

const TelegramAlerts = () => {
  const [activeTab, setActiveTab] = useState('definitions');
  const [alertDefinitions, setAlertDefinitions] = useState([]);
  const [alertRules, setAlertRules] = useState([]);
  const [alertSubscriptions, setAlertSubscriptions] = useState([]);
  const [telegramBots, setTelegramBots] = useState([]);
  const [telegramChannels, setTelegramChannels] = useState([]);
  const [discoveredEntities, setDiscoveredEntities] = useState([]);
  const [discoveryStats, setDiscoveryStats] = useState({});
  const [alertSearchTerm, setAlertSearchTerm] = useState('');
  
  // Modal states
  const [ruleModalVisible, setRuleModalVisible] = useState(false);
  const [subscriptionModalVisible, setSubscriptionModalVisible] = useState(false);
  const [throttleModalVisible, setThrottleModalVisible] = useState(false);
  const [botModalVisible, setBotModalVisible] = useState(false);
  const [channelModalVisible, setChannelModalVisible] = useState(false);
  const [alertDetailsModalVisible, setAlertDetailsModalVisible] = useState(false);

  // Debug modal state
  React.useEffect(() => {
    console.log('alertDetailsModalVisible changed:', alertDetailsModalVisible);
  }, [alertDetailsModalVisible]);
  
  const [formData, setFormData] = useState({});
  const [subscriptionFormData, setSubscriptionFormData] = useState({});
  const [throttleFormData, setThrottleFormData] = useState({});
  const [botFormData, setBotFormData] = useState({});
  const [channelFormData, setChannelFormData] = useState({});
  const [alertDetails, setAlertDetails] = useState({});

  const [editingRule, setEditingRule] = useState(null);
  const [editingSubscription, setEditingSubscription] = useState(null);
  const [editingThrottle, setEditingThrottle] = useState(null);
  const [editingBot, setEditingBot] = useState(null);
  const [editingChannel, setEditingChannel] = useState(null);

  // Load alert definitions
  const loadAlertDefinitions = async () => {
    try {
      const token = localStorage.getItem('token');
      console.log('Loading alert definitions...');
      console.log('API URL:', `${getApiBaseUrl()}/super/alerts/definitions`);
      console.log('Token exists:', !!token);
      
      const response = await fetch(`${getApiBaseUrl()}/super/alerts/definitions`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);
      
      const data = await response.json();
      console.log('Response data:', data);
      
      if (data.status === 'success') {
        setAlertDefinitions(data.data);
        console.log('Alert definitions loaded:', data.data.length);
      } else {
        console.error('API returned error:', data);
      }
    } catch (error) {
      console.error('Failed to load alert definitions:', error);
    }
  };

  // Load alert definition details
  const loadAlertDetails = async (alertCode) => {
    console.log('Loading alert details for:', alertCode);
    
    // First try to find the alert in the existing list
    const existingAlert = alertDefinitions.find(alert => alert.alert_code === alertCode);
    if (existingAlert) {
      console.log('Found alert in existing list:', existingAlert);
      setAlertDetails(existingAlert);
      setAlertDetailsModalVisible(true);
      return;
    }
    
    // If not found, try to fetch from API
    try {
      const token = localStorage.getItem('token');
      console.log('Token exists:', !!token);
      console.log('Token length:', token?.length || 0);
      
      const url = `${getApiBaseUrl()}/super/alerts/definitions/${alertCode}`;
      console.log('Fetching from URL:', url);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);
      
      // Try to get more error details
      const responseText = await response.text();
      console.log('Response body:', responseText);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}, body: ${responseText}`);
      }
      
      const data = JSON.parse(responseText);
      console.log('Response data:', data);
      
      if (data.status === 'success') {
        console.log('Setting alert details:', data.data);
        setAlertDetails(data.data);
        console.log('Setting modal visible to true');
        setAlertDetailsModalVisible(true);
      } else {
        console.error('Failed to load alert details:', data);
        // Show error message to user
        alert('Failed to load alert details: ' + (data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Failed to load alert details:', error);
      // Show error message to user
      alert('Failed to load alert details. Please check the console for more information.');
    }
  };

  // Load alert rules
  const loadAlertRules = async () => {
    try {
      console.log('Loading alert rules...');
      const response = await fetch(`${getApiBaseUrl()}/super/alerts/rules`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      console.log('Alert rules response status:', response.status);
      const data = await response.json();
      if (data.status === 'success') {
        setAlertRules(data.data);
        console.log('Alert rules loaded:', data.data.length);
      } else {
        console.error('Alert rules API error:', data);
      }
    } catch (error) {
      console.error('Failed to load alert rules:', error);
    }
  };

  // Load alert subscriptions
  const loadAlertSubscriptions = async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/super/alerts/subscriptions`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.status === 'success') {
        setAlertSubscriptions(data.data);
      }
    } catch (error) {
      console.error('Failed to load alert subscriptions');
    }
  };

  // Load Telegram bots
  const loadTelegramBots = async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/super/alerts/telegram/bots`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setTelegramBots(data.data);
      }
    } catch (error) {
      console.error('Failed to load Telegram bots');
    }
  };

  // Load Telegram channels
  const loadTelegramChannels = async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/super/alerts/telegram/channels`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setTelegramChannels(data.data);
      }
    } catch (error) {
      console.error('Failed to load Telegram channels');
    }
  };

  // Load discovered entities
  const loadDiscoveredEntities = async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/super/alerts/discovered`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.status === 'success') {
        setDiscoveredEntities(data.data);
      }
    } catch (error) {
      console.error('Failed to load discovered entities');
    }
  };

  // Load discovery statistics
  const loadDiscoveryStats = async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/super/alerts/discovered/stats`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.status === 'success') {
        setDiscoveryStats(data.data);
      }
    } catch (error) {
      console.error('Failed to load discovery stats');
    }
  };

  // Approve discovered bot
  const approveDiscoveredBot = async (entityId) => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/super/alerts/discovered/bot/${entityId}/approve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.status === 'success') {
        alert(data.message);
        loadDiscoveredEntities();
        loadDiscoveryStats();
        loadTelegramBots();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Failed to approve bot:', error);
      alert('Failed to approve bot');
    }
  };

  // Approve discovered channel
  const approveDiscoveredChannel = async (entityId) => {
    try {
      // Get selected bot ID
      const botId = prompt('Select Bot ID to associate with this channel:\n' + 
        telegramBots.map(bot => `${bot.id}: ${bot.bot_name}`).join('\n'));
      
      if (!botId) return;

      const response = await fetch(`${getApiBaseUrl()}/super/alerts/discovered/channel/${entityId}/approve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ bot_id: parseInt(botId) })
      });
      const data = await response.json();
      if (data.status === 'success') {
        alert(data.message);
        loadDiscoveredEntities();
        loadDiscoveryStats();
        loadTelegramChannels();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Failed to approve channel:', error);
      alert('Failed to approve channel');
    }
  };

  // Ignore discovered entity
  const ignoreDiscoveredEntity = async (entityId) => {
    if (!window.confirm('Are you sure you want to ignore this entity?')) {
      return;
    }
    
    try {
      const response = await fetch(`${getApiBaseUrl()}/super/alerts/discovered/${entityId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.status === 'success') {
        loadDiscoveredEntities();
        loadDiscoveryStats();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Failed to ignore entity:', error);
      alert('Failed to ignore entity');
    }
  };

  useEffect(() => {
    loadAlertDefinitions();
    loadAlertRules();
    loadAlertSubscriptions();
    loadTelegramBots();
    loadTelegramChannels();
  }, []);

  // Load discovered entities when tab switches to discovered
  React.useEffect(() => {
    if (activeTab === 'discovered') {
      loadDiscoveredEntities();
      loadDiscoveryStats();
    }
  }, [activeTab]);

  // Save alert rule
  const saveAlertRule = async () => {
    try {
      const url = editingRule 
        ? `${getApiBaseUrl()}/super/alerts/rules/${editingRule.id}`
        : `${getApiBaseUrl()}/super/alerts/rules`;
      
      const response = await fetch(url, {
        method: editingRule ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      if (data.status === 'success') {
        setRuleModalVisible(false);
        setFormData({});
        setEditingRule(null);
        loadAlertRules();
      }
    } catch (error) {
      console.error('Failed to save rule');
    }
  };

  // Save alert subscription
  const saveAlertSubscription = async () => {
    try {
      const url = editingSubscription 
        ? `${getApiBaseUrl()}/super/alerts/subscriptions/${editingSubscription.id}`
        : `${getApiBaseUrl()}/super/alerts/subscriptions`;
      
      const response = await fetch(url, {
        method: editingSubscription ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(subscriptionFormData)
      });

      const data = await response.json();
      if (data.status === 'success') {
        setSubscriptionModalVisible(false);
        setSubscriptionFormData({});
        setEditingSubscription(null);
        loadAlertSubscriptions();
      }
    } catch (error) {
      console.error('Failed to save subscription');
    }
  };

  // Delete alert rule
  const deleteAlertRule = async (id) => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/super/alerts/rules/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();
      if (data.status === 'success') {
        loadAlertRules();
      }
    } catch (error) {
      console.error('Failed to delete rule');
    }
  };

  // Delete alert subscription
  const deleteAlertSubscription = async (id) => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/super/alerts/subscriptions/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();
      if (data.status === 'success') {
        loadAlertSubscriptions();
      }
    } catch (error) {
      console.error('Failed to delete subscription');
    }
  };

  // Update throttle
  const updateThrottle = async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/super/alerts/throttle/${editingThrottle.alert_code}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(throttleFormData)
      });

      const data = await response.json();
      if (data.status === 'success') {
        setThrottleModalVisible(false);
        setThrottleFormData({});
        setEditingThrottle(null);
        loadAlertDefinitions();
      }
    } catch (error) {
      console.error('Failed to update throttle');
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'CRITICAL': return 'text-red-600 bg-red-100';
      case 'HIGH': return 'text-orange-600 bg-orange-100';
      case 'MEDIUM': return 'text-yellow-600 bg-yellow-100';
      default: return 'text-green-600 bg-green-100';
    }
  };

  const filteredAlertDefinitions = alertDefinitions.filter(alert => {
    const searchLower = alertSearchTerm.toLowerCase();
    return !alertSearchTerm || 
      alert.alert_code.toLowerCase().includes(searchLower) ||
      alert.title.toLowerCase().includes(searchLower) ||
      alert.category.toLowerCase().includes(searchLower) ||
      alert.severity.toLowerCase().includes(searchLower);
  });

  // Save Bot (Create or Update)
  const saveBot = async () => {
    try {
      const token = localStorage.getItem('token');
      const url = editingBot 
        ? `${getApiBaseUrl()}/super/alerts/bots/${editingBot.id}`
        : `${getApiBaseUrl()}/super/alerts/bots`;
      
      const response = await fetch(url, {
        method: editingBot ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          bot_name: botFormData.bot_name,
          bot_token: botFormData.bot_token,
          bot_username: botFormData.bot_username || '',
          is_active: botFormData.is_active
        })
      });
      
      const data = await response.json();
      if (data.status === 'success') {
        setBotModalVisible(false);
        setEditingBot(null);
        setBotFormData({});
        loadTelegramBots();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Failed to save bot:', error);
      alert('Failed to save bot');
    }
  };

  // Delete Telegram Bot
  const deleteTelegramBot = async (botId) => {
    if (!window.confirm('Are you sure you want to delete this bot?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${getApiBaseUrl()}/super/alerts/bots/${botId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      if (data.success) {
        loadTelegramBots();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Failed to delete bot:', error);
      alert('Failed to delete bot');
    }
  };

  // Test Telegram Bot
  const testTelegramBot = async (botId) => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/super/alerts/telegram/bots/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ bot_id: botId })
      });
      
      const data = await response.json();
      if (data.success) {
        alert(`Bot test successful!\n${data.message}\nChat ID: ${data.chat_id || 'N/A'}`);
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Failed to test bot:', error);
      alert('Failed to send test message');
    }
  };

  // Test Telegram Channel
  const testTelegramChannel = async (channelId) => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/super/alerts/telegram/channels/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ channel_id: channelId })
      });
      
      const data = await response.json();
      if (data.success) {
        alert(`Channel test successful!\nStatus: ${data.status}\n${data.message}`);
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Failed to test channel:', error);
      alert('Failed to send test message');
    }
  };

  // Save Channel (Create or Update)
  const saveChannel = async () => {
    try {
      const token = localStorage.getItem('token');
      const url = editingChannel 
        ? `${getApiBaseUrl()}/super/alerts/channels/${editingChannel.id}`
        : `${getApiBaseUrl()}/super/alerts/channels`;
      
      // Transform data to match backend expectations
      const requestData = {
        channel_name: channelFormData.name,
        channel_id: channelFormData.chat_id,
        bot_id: channelFormData.bot_id,
        is_active: channelFormData.is_active
      };
      
      const response = await fetch(url, {
        method: editingChannel ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestData)
      });
      
      const data = await response.json();
      if (data.status === 'success') {
        setChannelModalVisible(false);
        setEditingChannel(null);
        setChannelFormData({});
        loadTelegramChannels();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Failed to save channel:', error);
      alert('Failed to save channel');
    }
  };

  // Delete Telegram Channel
  const deleteTelegramChannel = async (channelId) => {
    if (!window.confirm('Are you sure you want to delete this channel?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${getApiBaseUrl()}/super/alerts/channels/${channelId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      if (data.status === 'success') {
        loadTelegramChannels();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Failed to delete channel:', error);
      alert('Failed to delete channel');
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Icons.Bell className="w-6 h-6" />
          Telegram Alerts Management
        </h1>
        <p className="text-gray-600 mt-2">Configure and manage Telegram alert notifications</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {['definitions', 'rules', 'subscriptions', 'bots', 'channels', 'discovered', 'critical'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab === 'critical' ? '⚡ Critical Alerts' : tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </nav>
      </div>

      {/* Alert Definitions Tab */}
      {activeTab === 'definitions' && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b border-gray-200">
            <div className="relative">
              <UniversalSearchInput
                value={alertSearchTerm}
                onChange={(e) => setAlertSearchTerm(e.target.value)}
                placeholder="Search alert code / title"
                className="w-64"
              />
              {alertSearchTerm && (
                <button
                  onClick={() => setAlertSearchTerm('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <Icons.X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Alert Code</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Severity</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Throttle (Seconds)</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Default Enabled</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredAlertDefinitions.map((alert) => (
                  <tr key={alert.alert_code}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{alert.alert_code}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                        {alert.category || 'General'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`px-2 py-1 text-xs rounded-full ${getSeverityColor(alert.severity)}`}>
                        {alert.severity}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{alert.title}</td>
                    <td className="px-6 py-4 text-sm text-gray-500 truncate max-w-xs" title={alert.description}>{alert.description}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{alert.throttle_seconds}s</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                        alert.default_enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {alert.default_enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <button
                        onClick={() => {
                          console.log('View Details clicked for alert:', alert.alert_code);
                          loadAlertDetails(alert.alert_code);
                        }}
                        className="text-indigo-600 hover:text-indigo-900 flex items-center gap-1"
                        title="View details"
                      >
                        <Icons.Eye className="w-4 h-4" />
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Alert Rules Tab */}
      {activeTab === 'rules' && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b border-gray-200">
            <button
              onClick={() => {
                setEditingRule(null);
                setFormData({});
                setRuleModalVisible(true);
              }}
              className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center gap-2"
            >
              <Icons.Plus className="w-4 h-4" />
              Add Alert Rule
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rule ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Alert Code</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rule Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Conditions</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Enabled</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Updated</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {alertRules.map((rule) => (
                  <tr key={rule.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">#{rule.id}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{rule.alert_code}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{rule.rule_name}</td>
                    <td className="px-6 py-4 text-sm text-gray-500 truncate max-w-xs" title={rule.condition_json}>
                      <pre className="text-xs bg-gray-50 p-1 rounded overflow-hidden">
                        {JSON.stringify(JSON.parse(rule.condition_json || '{}'), null, 2)}
                      </pre>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                        rule.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {rule.is_active ? 'Enabled' : 'Disabled'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {rule.created_at ? new Date(rule.created_at).toLocaleString() : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {rule.updated_at ? new Date(rule.updated_at).toLocaleString() : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setEditingRule(rule);
                            setFormData(rule);
                            setRuleModalVisible(true);
                          }}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="Edit rule"
                        >
                          <Icons.Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => deleteAlertRule(rule.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete rule"
                        >
                          <Icons.Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Alert Subscriptions Tab */}
      {activeTab === 'subscriptions' && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b border-gray-200">
            <button
              onClick={() => {
                setEditingSubscription(null);
                setSubscriptionFormData({});
                setSubscriptionModalVisible(true);
              }}
              className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center gap-2"
            >
              <Icons.Plus className="w-4 h-4" />
              Add Subscription
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subscription ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Alert Code</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Channel ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Channel Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Chat ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Enabled</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Updated</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {alertSubscriptions.map((subscription) => (
                  <tr key={subscription.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">#{subscription.id}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{subscription.alert_code}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{subscription.channel_id}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{subscription.channel_name || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{subscription.chat_id || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                        subscription.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {subscription.is_active ? 'Enabled' : 'Disabled'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {subscription.created_at ? new Date(subscription.created_at).toLocaleString() : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {subscription.updated_at ? new Date(subscription.updated_at).toLocaleString() : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setEditingSubscription(subscription);
                            setSubscriptionFormData(subscription);
                            setSubscriptionModalVisible(true);
                          }}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="Edit subscription"
                        >
                          <Icons.Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => deleteAlertSubscription(subscription.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete subscription"
                        >
                          <Icons.Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Telegram Bots Tab */}
      {activeTab === 'bots' && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">Telegram Bots</h3>
            <button
              onClick={() => {
                setEditingBot(null);
                setBotFormData({
                  bot_name: '',
                  bot_token: '',
                  bot_username: '',
                  is_active: true
                });
                setBotModalVisible(true);
              }}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <Icons.Plus className="w-4 h-4 mr-2" />
              Add Bot
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bot Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bot Username</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bot Token</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Updated</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {telegramBots.map((bot) => (
                  <tr key={bot.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {bot.id || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {bot.bot_name || 'Unnamed Bot'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {bot.bot_username ? `@${bot.bot_username}` : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center">
                        <code className="bg-gray-100 px-2 py-1 rounded text-xs">
                          {bot.bot_token ? 
                            (bot.bot_token.length > 20 ? 
                              `${bot.bot_token.substring(0, 8)}...${bot.bot_token.substring(bot.bot_token.length - 4)}` : 
                              bot.bot_token
                            ) : 
                            'No token'
                          }
                        </code>
                        {bot.bot_token && bot.bot_token !== 'SET_TOKEN_HERE' && (
                          <Icons.Check className="w-4 h-4 text-green-500 ml-2" title="Valid token format" />
                        )}
                        {bot.bot_token === 'SET_TOKEN_HERE' && (
                          <Icons.AlertTriangle className="w-4 h-4 text-yellow-500 ml-2" title="Placeholder token" />
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                        bot.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {bot.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {bot.created_at ? 
                        (typeof bot.created_at === 'string' ? 
                          new Date(bot.created_at).toLocaleString() : 
                          new Date(bot.created_at * 1000).toLocaleString()
                        ) : 
                        '-'
                      }
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {bot.updated_at ? 
                        (typeof bot.updated_at === 'string' ? 
                          new Date(bot.updated_at).toLocaleString() : 
                          new Date(bot.updated_at * 1000).toLocaleString()
                        ) : 
                        '-'
                      }
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => testTelegramBot(bot.id)}
                        className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 mr-2"
                      >
                        <Icons.Send className="w-3 h-3 mr-1" />
                        Test
                      </button>
                      <button
                        onClick={async () => {
                          try {
                            const token = localStorage.getItem('token');
                            const response = await fetch(`${getApiBaseUrl()}/super/alerts/telegram/bots/${bot.id}`, {
                              headers: {
                                'Authorization': `Bearer ${token}`
                              }
                            });
                            const data = await response.json();
                            if (data.success) {
                              setEditingBot(data.data);
                              setBotFormData({
                                bot_name: data.data.bot_name,
                                bot_token: data.data.bot_token,
                                bot_username: data.data.bot_username,
                                is_active: data.data.is_active
                              });
                              setBotModalVisible(true);
                            } else {
                              alert('Error: ' + data.message);
                            }
                          } catch (error) {
                            console.error('Failed to fetch bot details:', error);
                            alert('Failed to fetch bot details');
                          }
                        }}
                        className="text-indigo-600 hover:text-indigo-900 mr-2"
                        title="Edit bot"
                      >
                        <Icons.Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deleteTelegramBot(bot.id)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete bot"
                      >
                        <Icons.Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Telegram Channels Tab */}
      {activeTab === 'channels' && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">Telegram Channels</h3>
            <button
              onClick={() => {
                setEditingChannel(null);
                setChannelFormData({
                  name: '',
                  chat_id: '',
                  type: 'channel',
                  bot_id: '',
                  is_active: true
                });
                setChannelModalVisible(true);
              }}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <Icons.Plus className="w-4 h-4 mr-2" />
              Add Channel
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Chat ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {telegramChannels.map((channel) => (
                  <tr key={channel.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{channel.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-mono">{channel.chat_id}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                        channel.type === 'group' ? 'bg-purple-100 text-purple-800' :
                        channel.type === 'channel' ? 'bg-blue-100 text-blue-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {channel.type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                        channel.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {channel.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => testTelegramChannel(channel.id)}
                        className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 mr-2"
                      >
                        <Icons.Send className="w-3 h-3 mr-1" />
                        Test
                      </button>
                      <button
                        onClick={async () => {
                          try {
                            const token = localStorage.getItem('token');
                            const response = await fetch(`${getApiBaseUrl()}/super/alerts/telegram/channels/${channel.id}`, {
                              headers: {
                                'Authorization': `Bearer ${token}`
                              }
                            });
                            const data = await response.json();
                            if (data.success) {
                              setEditingChannel(data.data);
                              setChannelFormData({
                                name: data.data.name,
                                chat_id: data.data.chat_id,
                                type: data.data.type,
                                bot_id: data.data.bot_id,
                                is_active: data.data.is_active
                              });
                              setChannelModalVisible(true);
                            } else {
                              alert('Error: ' + data.message);
                            }
                          } catch (error) {
                            console.error('Failed to fetch channel details:', error);
                            alert('Failed to fetch channel details');
                          }
                        }}
                        className="text-indigo-600 hover:text-indigo-900 mr-2"
                        title="Edit channel"
                      >
                        <Icons.Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deleteTelegramChannel(channel.id)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete channel"
                      >
                        <Icons.Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Alert Rule Modal */}
      {ruleModalVisible && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingRule ? 'Edit Alert Rule' : 'Add Alert Rule'}
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Alert Code</label>
                  <select
                    value={formData.alert_code || ''}
                    onChange={(e) => setFormData({ ...formData, alert_code: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">Select alert code</option>
                    {alertDefinitions.map((alert) => (
                      <option key={alert.alert_code} value={alert.alert_code}>
                        {alert.alert_code} — {alert.title}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Telegram Bot</label>
                  <select
                    value={formData.bot_id || ''}
                    onChange={(e) => setFormData({ ...formData, bot_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">Select bot</option>
                    {telegramBots.map((bot) => (
                      <option key={bot.id} value={bot.id}>{bot.bot_name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Telegram Channel</label>
                  <select
                    value={formData.channel_id || ''}
                    onChange={(e) => setFormData({ ...formData, channel_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">Select channel</option>
                    {telegramChannels.map((channel) => (
                      <option key={channel.id} value={channel.id}>{channel.channel_name}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.enabled || false}
                    onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-900">Enabled</label>
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <button
                  onClick={() => {
                    setRuleModalVisible(false);
                    setFormData({});
                    setEditingRule(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={saveAlertRule}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
                >
                  {editingRule ? 'Update' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Alert Subscription Modal */}
      {subscriptionModalVisible && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingSubscription ? 'Edit Alert Subscription' : 'Add Alert Subscription'}
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subscriber Type</label>
                  <select
                    value={subscriptionFormData.subscriber_type || ''}
                    onChange={(e) => setSubscriptionFormData({ ...subscriptionFormData, subscriber_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">Select type</option>
                    <option value="user">User</option>
                    <option value="entity">Entity</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subscriber ID</label>
                  <input
                    type="number"
                    value={subscriptionFormData.subscriber_id || ''}
                    onChange={(e) => setSubscriptionFormData({ ...subscriptionFormData, subscriber_id: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Alert Code</label>
                  <select
                    value={subscriptionFormData.alert_code || ''}
                    onChange={(e) => setSubscriptionFormData({ ...subscriptionFormData, alert_code: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">Select alert code</option>
                    {alertDefinitions.map((alert) => (
                      <option key={alert.alert_code} value={alert.alert_code}>
                        {alert.alert_code} — {alert.title}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Telegram Bot</label>
                  <select
                    value={subscriptionFormData.bot_id || ''}
                    onChange={(e) => setSubscriptionFormData({ ...subscriptionFormData, bot_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">Select bot</option>
                    {telegramBots.map((bot) => (
                      <option key={bot.id} value={bot.id}>{bot.bot_name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Telegram Channel</label>
                  <select
                    value={subscriptionFormData.channel_id || ''}
                    onChange={(e) => setSubscriptionFormData({ ...subscriptionFormData, channel_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">Select channel</option>
                    {telegramChannels.map((channel) => (
                      <option key={channel.id} value={channel.id}>{channel.channel_name}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={subscriptionFormData.enabled || false}
                    onChange={(e) => setSubscriptionFormData({ ...subscriptionFormData, enabled: e.target.checked })}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-900">Enabled</label>
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <button
                  onClick={() => {
                    setSubscriptionModalVisible(false);
                    setSubscriptionFormData({});
                    setEditingSubscription(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={saveAlertSubscription}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
                >
                  {editingSubscription ? 'Update' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Throttle Update Modal */}
      {throttleModalVisible && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Update Throttle Settings</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Throttle (Seconds)</label>
                  <input
                    type="number"
                    min="0"
                    value={throttleFormData.throttle_seconds || ''}
                    onChange={(e) => setThrottleFormData({ ...throttleFormData, throttle_seconds: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Minimum 0 seconds</p>
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <button
                  onClick={() => {
                    setThrottleModalVisible(false);
                    setThrottleFormData({});
                    setEditingThrottle(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={updateThrottle}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
                >
                  Update
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bot Modal */}
      {botModalVisible && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingBot ? 'Edit Bot' : 'Add New Bot'}
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bot Name</label>
                  <input
                    type="text"
                    value={botFormData.bot_name || ''}
                    onChange={(e) => setBotFormData({ ...botFormData, bot_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Enter bot name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bot Token</label>
                  <input
                    type="text"
                    value={botFormData.bot_token || ''}
                    onChange={(e) => setBotFormData({ ...botFormData, bot_token: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Enter bot token from @BotFather"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bot Username</label>
                  <input
                    type="text"
                    value={botFormData.bot_username || ''}
                    onChange={(e) => setBotFormData({ ...botFormData, bot_username: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="@bot_username"
                  />
                </div>
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={botFormData.is_active || false}
                      onChange={(e) => setBotFormData({ ...botFormData, is_active: e.target.checked })}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Active</span>
                  </label>
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <button
                  onClick={() => {
                    setBotModalVisible(false);
                    setEditingBot(null);
                    setBotFormData({});
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={saveBot}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
                >
                  {editingBot ? 'Update' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Channel Modal */}
      {channelModalVisible && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingChannel ? 'Edit Channel' : 'Add New Channel'}
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Channel Name</label>
                  <input
                    type="text"
                    value={channelFormData.name || ''}
                    onChange={(e) => setChannelFormData({ ...channelFormData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Enter channel name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Chat ID</label>
                  <input
                    type="text"
                    value={channelFormData.chat_id || ''}
                    onChange={(e) => setChannelFormData({ ...channelFormData, chat_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Enter chat ID (e.g., -1001234567890)"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bot</label>
                  <select
                    value={channelFormData.bot_id || ''}
                    onChange={(e) => setChannelFormData({ ...channelFormData, bot_id: e.target.value ? parseInt(e.target.value) : '' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">Select a bot</option>
                    {telegramBots.map((bot) => (
                      <option key={bot.id} value={bot.id}>
                        {bot.bot_name} (@{bot.bot_username})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select
                    value={channelFormData.type || 'channel'}
                    onChange={(e) => setChannelFormData({ ...channelFormData, type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="channel">Channel</option>
                    <option value="group">Group</option>
                    <option value="private">Private</option>
                  </select>
                </div>
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={channelFormData.is_active || false}
                      onChange={(e) => setChannelFormData({ ...channelFormData, is_active: e.target.checked })}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Active</span>
                  </label>
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <button
                  onClick={() => {
                    setChannelModalVisible(false);
                    setEditingChannel(null);
                    setChannelFormData({});
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={saveChannel}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
                >
                  {editingChannel ? 'Update' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Discovered Entities Tab */}
      {activeTab === 'discovered' && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Discovered Telegram Entities</h3>
            <p className="text-sm text-gray-600 mt-1">Bots, channels, and groups discovered from Telegram updates</p>
          </div>
          
          {/* Discovery Stats */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">{discoveryStats.total_discovered || 0}</div>
                <div className="text-sm text-gray-600">Total Discovered</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{discoveryStats.total_approved || 0}</div>
                <div className="text-sm text-gray-600">Approved</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-600">{discoveryStats.bots_discovered || 0}</div>
                <div className="text-sm text-gray-600">Bots Found</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{discoveryStats.channels_found || 0}</div>
                <div className="text-sm text-gray-600">Channels/Groups</div>
              </div>
            </div>
          </div>

          {/* Discovered Entities Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Entity Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Telegram ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name / Title</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Username</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source Bot</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {discoveredEntities.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                      No discovered entities found. Make sure your bots are active and receiving updates.
                    </td>
                  </tr>
                ) : (
                  discoveredEntities.map((entity) => (
                    <tr key={entity.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          entity.entity_type === 'bot' ? 'bg-purple-100 text-purple-800' :
                          entity.entity_type === 'channel' ? 'bg-blue-100 text-blue-800' :
                          entity.entity_type === 'group' || entity.entity_type === 'supergroup' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {entity.entity_type.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {entity.telegram_id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {entity.title || entity.name || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {entity.username ? `@${entity.username}` : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {entity.source_bot_id || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {entity.approved ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Approved
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                            Pending
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {entity.approved ? (
                          <span className="text-gray-400">Already Added</span>
                        ) : (
                          <div className="flex space-x-2">
                            {entity.entity_type === 'bot' ? (
                              <button
                                onClick={() => approveDiscoveredBot(entity.id)}
                                className="text-indigo-600 hover:text-indigo-900"
                                title="Approve & Add Bot"
                              >
                                <Icons.Check className="w-4 h-4" />
                              </button>
                            ) : entity.entity_type === 'channel' || entity.entity_type === 'group' || entity.entity_type === 'supergroup' ? (
                              <button
                                onClick={() => approveDiscoveredChannel(entity.id)}
                                className="text-indigo-600 hover:text-indigo-900"
                                title="Approve & Add Channel"
                              >
                                <Icons.Check className="w-4 h-4" />
                              </button>
                            ) : null}
                            <button
                              onClick={() => ignoreDiscoveredEntity(entity.id)}
                              className="text-red-600 hover:text-red-900"
                              title="Ignore"
                            >
                              <Icons.X className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* PHASE-8: Critical Alerts Configuration Tab */}
      {activeTab === 'critical' && (
        <div className="space-y-6">
          {/* Overview Card */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200" style={{ background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.05), rgba(245, 158, 11, 0.05))' }}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #dc2626, #f59e0b)' }}>
                  <Icons.Zap className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Critical Error Alerts (Phase 8)</h3>
                  <p className="text-sm text-gray-600">Real-time Telegram notifications for CRITICAL system errors</p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* How it works */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <Icons.Info className="w-4 h-4 text-indigo-500" />
                  How It Works
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 rounded-lg border border-gray-200">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center text-xs font-bold text-red-600">1</div>
                      <span className="text-sm font-medium text-gray-700">Error Detected</span>
                    </div>
                    <p className="text-xs text-gray-500">When a CRITICAL error occurs (panic, DB failure, Kafka down), the global error handler captures it with full stack trace.</p>
                  </div>
                  <div className="p-4 rounded-lg border border-gray-200">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center text-xs font-bold text-amber-600">2</div>
                      <span className="text-sm font-medium text-gray-700">Rate Limited</span>
                    </div>
                    <p className="text-xs text-gray-500">Alerts are throttled to max <strong>1 per 5 minutes</strong> per service:function to prevent Telegram API flooding during cascading failures.</p>
                  </div>
                  <div className="p-4 rounded-lg border border-gray-200">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center text-xs font-bold text-green-600">3</div>
                      <span className="text-sm font-medium text-gray-700">Telegram Sent</span>
                    </div>
                    <p className="text-xs text-gray-500">HTML-formatted message sent via Bot API with service name, function, error details, hostname, and timestamp.</p>
                  </div>
                </div>
              </div>

              {/* Environment Variables */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <Icons.Settings className="w-4 h-4 text-indigo-500" />
                  Environment Variables Required
                </h4>
                <div className="bg-gray-900 rounded-lg p-4 font-mono text-sm overflow-x-auto">
                  <div className="text-gray-400"># Add to /etc/systemd/system/gosmpp-backend.service [Service] section</div>
                  <div className="text-gray-400"># or export in your shell before starting the backend</div>
                  <div className="mt-2">
                    <span className="text-cyan-400">TELEGRAM_BOT_TOKEN</span><span className="text-gray-500">=</span><span className="text-green-400">"your-bot-token-from-@BotFather"</span>
                  </div>
                  <div>
                    <span className="text-cyan-400">TELEGRAM_CHAT_ID</span><span className="text-gray-500">=</span><span className="text-green-400">"-100xxxxxxxxxx"</span>
                    <span className="text-gray-500 ml-2"># group/channel ID</span>
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  Get your bot token from <strong>@BotFather</strong> on Telegram. Get chat ID by adding the bot to a group and calling 
                  <code className="px-1 py-0.5 bg-gray-100 rounded text-[10px] ml-1">https://api.telegram.org/bot{'<token>'}/getUpdates</code>
                </p>
              </div>

              {/* Systemd Example */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <Icons.Terminal className="w-4 h-4 text-indigo-500" />
                  Systemd Service Configuration
                </h4>
                <div className="bg-gray-900 rounded-lg p-4 font-mono text-xs overflow-x-auto">
                  <div className="text-gray-400"># sudo systemctl edit gosmpp-backend</div>
                  <div className="text-yellow-400 mt-1">[Service]</div>
                  <div><span className="text-cyan-400">Environment</span>=<span className="text-green-400">"TELEGRAM_BOT_TOKEN=123456:ABC-DEF..."</span></div>
                  <div><span className="text-cyan-400">Environment</span>=<span className="text-green-400">"TELEGRAM_CHAT_ID=-1001234567890"</span></div>
                  <div className="text-gray-400 mt-2"># Then: sudo systemctl daemon-reload && sudo systemctl restart gosmpp-backend</div>
                </div>
              </div>

              {/* Alert Message Format */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <Icons.MessageSquare className="w-4 h-4 text-indigo-500" />
                  Alert Message Format
                </h4>
                <div className="p-4 rounded-lg border border-gray-200" style={{ background: 'rgba(99, 102, 241, 0.02)' }}>
                  <div className="text-sm space-y-1">
                    <p className="font-bold text-red-600">🚨 WAY2CONNECT CRITICAL ERROR</p>
                    <p className="text-gray-700"><strong>Service:</strong> <span className="text-gray-500">SMSWorker</span></p>
                    <p className="text-gray-700"><strong>Function:</strong> <span className="text-gray-500">ProcessBatch</span></p>
                    <p className="text-gray-700"><strong>Error:</strong> <span className="text-gray-500">PANIC: runtime error: index out of range</span></p>
                    <p className="text-gray-700"><strong>Host:</strong> <span className="text-gray-500">ip219.ip-148-113-24.net</span></p>
                    <p className="text-gray-700"><strong>Time:</strong> <span className="text-gray-500">2026-02-11 23:45:00 IST</span></p>
                  </div>
                </div>
              </div>

              {/* Features Grid */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <Icons.Shield className="w-4 h-4 text-indigo-500" />
                  Features
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { icon: 'Zap', label: 'Real-time', desc: 'Instant Telegram delivery' },
                    { icon: 'Clock', label: 'Rate Limited', desc: '1 per 5min per error type' },
                    { icon: 'Code', label: 'HTML Formatted', desc: 'Rich message with bold/code' },
                    { icon: 'Server', label: 'Hostname', desc: 'Multi-server identification' },
                  ].map((feat, i) => {
                    const Icon = Icons[feat.icon];
                    return (
                      <div key={i} className="p-3 rounded-lg border border-gray-200 text-center">
                        {Icon && <Icon className="w-5 h-5 text-indigo-400 mx-auto mb-1" />}
                        <p className="text-xs font-semibold text-gray-700">{feat.label}</p>
                        <p className="text-[10px] text-gray-400">{feat.desc}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Alert Details Modal */}
      {alertDetailsModalVisible && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-6xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-3">
                <h3 className="text-xl font-bold text-gray-900">
                  Alert Details: {alertDetails.alert_code || 'Loading...'}
                </h3>
                <span className={`px-3 py-1 text-sm rounded-full ${getSeverityColor(alertDetails.severity)}`}>
                  {alertDetails.severity}
                </span>
              </div>
              <button
                onClick={() => {
                  console.log('Closing modal');
                  setAlertDetailsModalVisible(false);
                  setAlertDetails({});
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <Icons.X className="w-6 h-6" />
              </button>
            </div>

            {alertDetails && Object.keys(alertDetails).length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Basic Info */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Icons.Info className="w-5 h-5" />
                    Basic Info
                  </h4>
                  <div className="space-y-2">
                    <div>
                      <span className="text-sm font-medium text-gray-500">Alert Code:</span>
                      <p className="text-sm text-gray-900">{alertDetails.alert_code}</p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">Category:</span>
                      <p className="text-sm text-gray-900">{alertDetails.category || 'General'}</p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">Severity:</span>
                      <p className="text-sm text-gray-900">{alertDetails.severity}</p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">Title:</span>
                      <p className="text-sm text-gray-900">{alertDetails.title}</p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">Throttle (Seconds):</span>
                      <p className="text-sm text-gray-900">{alertDetails.throttle_seconds}s</p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">Default Enabled:</span>
                      <p className="text-sm text-gray-900">
                        {alertDetails.default_enabled ? 'Enabled' : 'Disabled'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Icons.FileText className="w-5 h-5" />
                    Description
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <span className="text-sm font-medium text-gray-500">Short Description:</span>
                      <p className="text-sm text-gray-900 mt-1">{alertDetails.description}</p>
                    </div>
                    {alertDetails.detailed_description && (
                      <div>
                        <span className="text-sm font-medium text-gray-500">Detailed Description:</span>
                        <p className="text-sm text-gray-900 mt-1 whitespace-pre-wrap">
                          {alertDetails.detailed_description}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Additional fields if available */}
                {(alertDetails.trigger_conditions || alertDetails.sample_payload || alertDetails.event_source) && (
                  <div className="bg-gray-50 p-4 rounded-lg md:col-span-2">
                    <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <Icons.Settings className="w-5 h-5" />
                      Technical Details
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {alertDetails.event_source && (
                        <div>
                          <span className="text-sm font-medium text-gray-500">Event Source:</span>
                          <p className="text-sm text-gray-900 mt-1">{alertDetails.event_source}</p>
                        </div>
                      )}
                      {alertDetails.system_load_impact && (
                        <div>
                          <span className="text-sm font-medium text-gray-500">System Load Impact:</span>
                          <p className="text-sm text-gray-900 mt-1">{alertDetails.system_load_impact}</p>
                        </div>
                      )}
                      {alertDetails.created_at && (
                        <div>
                          <span className="text-sm font-medium text-gray-500">Created:</span>
                          <p className="text-sm text-gray-900 mt-1">
                            {new Date(alertDetails.created_at).toLocaleString()}
                          </p>
                        </div>
                      )}
                    </div>
                    {alertDetails.trigger_conditions && (
                      <div className="mt-4">
                        <span className="text-sm font-medium text-gray-500">Trigger Conditions:</span>
                        <pre className="text-xs bg-gray-100 p-2 rounded mt-1 overflow-x-auto">
                          {JSON.stringify(JSON.parse(alertDetails.trigger_conditions || '{}'), null, 2)}
                        </pre>
                      </div>
                    )}
                    {alertDetails.sample_payload && (
                      <div className="mt-4">
                        <span className="text-sm font-medium text-gray-500">Sample Payload:</span>
                        <pre className="text-xs bg-gray-100 p-2 rounded mt-1 overflow-x-auto">
                          {JSON.stringify(JSON.parse(alertDetails.sample_payload || '{}'), null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-gray-400 mb-2">
                  <Icons.AlertCircle className="h-12 w-12 mx-auto" />
                </div>
                <p className="text-sm text-gray-500">No alert details available</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TelegramAlerts;
